import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Shield, Loader2, Sparkles, ChevronDown, ChevronUp } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const DOSHAS = [
  { id: 'mangal', name: 'Mangal Dosha', nameHi: 'मंगल दोष', icon: '🔴', desc: 'Mars affliction affecting marriage' },
  { id: 'kaal_sarp', name: 'Kaal Sarp Dosha', nameHi: 'काल सर्प दोष', icon: '🐍', desc: 'All planets between Rahu-Ketu' },
  { id: 'shani', name: 'Shani Dosha', nameHi: 'शनि दोष', icon: '🪐', desc: 'Saturn\'s adverse effects' },
  { id: 'pitra', name: 'Pitra Dosha', nameHi: 'पितृ दोष', icon: '🙏', desc: 'Ancestral karmic debt' },
  { id: 'nazar', name: 'Nazar Dosha', nameHi: 'नजर दोष', icon: '🧿', desc: 'Evil eye affliction' },
  { id: 'grahan', name: 'Grahan Dosha', nameHi: 'ग्रहण दोष', icon: '🌑', desc: 'Eclipse-related affliction' },
  { id: 'general', name: 'General Remedies', nameHi: 'सामान्य उपाय', icon: '✨', desc: 'General spiritual remedies for peace' },
];

export default function RemediesPage({ user }) {
  const navigate = useNavigate();
  const [selectedDosha, setSelectedDosha] = useState(null);
  const [remedy, setRemedy] = useState(null);
  const [loading, setLoading] = useState(false);
  const [expandedSection, setExpandedSection] = useState(null);

  const fetchRemedy = async (dosha) => {
    setSelectedDosha(dosha);
    setLoading(true);
    setRemedy(null);
    try {
      const token = localStorage.getItem('dharmbook_token');
      const res = await axios.post(`${API}/jyotish/remedies`, { dosha: dosha.id, dosha_name: dosha.name }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setRemedy(res.data);
    } catch { setRemedy({ error: true }); }
    setLoading(false);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-teal-50 via-white to-cyan-50" data-testid="remedies-page">
      {/* Header */}
      <div className="bg-gradient-to-r from-[#8B2500] via-[#A0522D] to-[#8B4513] text-white py-6 px-4">
        <div className="max-w-2xl mx-auto">
          <button onClick={() => navigate('/jyotish')} className="flex items-center gap-1 text-orange-200 text-sm mb-3">
            <ArrowLeft className="w-4 h-4" /> Back to Jyotish
          </button>
          <div className="flex items-center gap-2">
            <Shield className="w-7 h-7 text-teal-300" />
            <h1 className="text-2xl font-bold" style={{ fontFamily: "'Yatra One', serif" }}>Remedies & Upaay</h1>
          </div>
          <p className="text-orange-200 text-sm mt-1">Vedic remedies for doshas and planetary afflictions</p>
        </div>
      </div>

      <div className="max-w-2xl mx-auto px-4 py-6">
        {/* Dosha Selection Grid */}
        <h3 className="text-sm font-bold text-slate-600 mb-3">Select a Dosha to get remedies:</h3>
        <div className="grid grid-cols-2 gap-3 mb-6">
          {DOSHAS.map(d => (
            <button
              key={d.id}
              onClick={() => fetchRemedy(d)}
              className={`flex items-center gap-3 p-3.5 rounded-2xl border-2 text-left transition-all ${
                selectedDosha?.id === d.id
                  ? 'border-[#8B2500] bg-orange-50 shadow-md'
                  : 'border-slate-100 bg-white hover:border-orange-200 hover:shadow-sm'
              }`}
              data-testid={`dosha-${d.id}`}
            >
              <span className="text-2xl">{d.icon}</span>
              <div className="min-w-0">
                <p className="text-sm font-bold text-slate-700 truncate">{d.name}</p>
                <p className="text-[10px] text-slate-400">{d.nameHi}</p>
              </div>
            </button>
          ))}
        </div>

        {/* Loading */}
        {loading && (
          <div className="flex flex-col items-center py-12">
            <Loader2 className="w-8 h-8 text-[#8B2500] animate-spin mb-3" />
            <p className="text-sm text-slate-500">Consulting Vedic texts for {selectedDosha?.nameHi}...</p>
          </div>
        )}

        {/* Result */}
        {remedy && !remedy.error && !loading && (
          <div className="bg-white rounded-2xl shadow-md border border-orange-100 overflow-hidden" data-testid="remedy-result">
            <div className="px-5 py-4 bg-gradient-to-r from-teal-50 to-cyan-50 border-b border-teal-100">
              <div className="flex items-center gap-3">
                <span className="text-3xl">{selectedDosha.icon}</span>
                <div>
                  <h3 className="font-bold text-[#8B2500] text-lg">{selectedDosha.name}</h3>
                  <p className="text-xs text-slate-500">{selectedDosha.nameHi} — Vedic Remedies</p>
                </div>
              </div>
            </div>
            <div className="divide-y divide-orange-50">
              {remedy.sections?.map((section, i) => (
                <div key={i}>
                  <button
                    onClick={() => setExpandedSection(expandedSection === i ? null : i)}
                    className="w-full flex items-center justify-between px-5 py-3.5 hover:bg-orange-50/50 transition-colors"
                  >
                    <div className="flex items-center gap-2">
                      <Sparkles className="w-4 h-4 text-[#8B2500]" />
                      <span className="text-sm font-bold text-slate-700">{section.title}</span>
                    </div>
                    {expandedSection === i ? <ChevronUp className="w-4 h-4 text-slate-400" /> : <ChevronDown className="w-4 h-4 text-slate-400" />}
                  </button>
                  {expandedSection === i && (
                    <div className="px-5 pb-4 text-sm text-slate-600 leading-relaxed whitespace-pre-line">{section.content}</div>
                  )}
                </div>
              ))}
              {remedy.general_advice && (
                <div className="px-5 py-4">
                  <h4 className="text-xs font-bold text-[#8B2500] uppercase mb-1">Important Note</h4>
                  <p className="text-sm text-slate-600">{remedy.general_advice}</p>
                </div>
              )}
            </div>
          </div>
        )}

        {remedy?.error && !loading && (
          <div className="text-center py-8"><p className="text-red-500">Failed to fetch remedies. Please try again.</p></div>
        )}
      </div>
    </div>
  );
}
